/**
 * Typed TanStack Query v5 hooks over the console API contract.
 *
 * The interfaces here mirror the Lambda handlers verbatim — they ARE the
 * contract:
 *   - reads:  infra/lib/lambdas/console-api/reads.ts
 *   - writes: infra/lib/lambdas/console-api/writes.ts
 *   - routes: infra/lib/lambdas/console-api/handler.ts
 *
 * Route reconciliation (handler.ts ROUTES):
 *   GET  /health
 *   GET  /settings
 *   GET  /context/{artifact}
 *   GET  /runs            (query param: `limit`, getRuns clamps to <=100, default 20)
 *   GET  /runs/{id}
 *   GET  /universe
 *   GET  /shadow          (open + recently-closed shadow ledger positions)
 *   GET  /strategy        (open + recently-closed STRATEGY ledger positions —
 *                          the executed counterpart of /shadow; empty until the
 *                          Phase 4 execution layer writes strategy_position# rows)
 *   PUT  /settings/{key}        body {"value": ...}
 *   PUT  /context/{artifact}    raw text body, optional ?status=draft|approved
 *   PUT  /universe/pool         body {"value": [...]}
 */

import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from './auth';
import { makeApi, type Api } from './api';

// ---------------------------------------------------------------------------
// Response shapes — mirror the Lambda handlers verbatim.
// ---------------------------------------------------------------------------

/** A row from GET /settings (reads.ts getSettings). `secret` rows carry
 *  `value: null` + a non-null `presence`; non-secret rows carry a resolved
 *  `value` + `presence: null`. */
export interface SettingRow {
  key: string;
  title: string;
  description: string;
  type:
    | 'number' | 'integer' | 'boolean' | 'string' | 'enum'
    | 'list' | 'table' | 'markdown' | 'json';
  unit: string | null;
  constraints: Record<string, unknown> | null;
  enforcement: 'hard' | 'soft' | 'advisory' | 'infra';
  store: string;
  change: string;
  editable: boolean;
  secret: boolean;
  value: unknown;
  source: string;
  presence: SecretPresence | null;
  framework_ref: string | null;
}

export interface SettingsResponse {
  settings: SettingRow[];
}

/** Secret presence metadata (reads.ts secretPresence) — also surfaced inline
 *  on secret SettingRows and as the `secrets` array on GET /health. */
export interface SecretPresence {
  set: boolean;
  lastModified: string | null;
}

export interface HealthSecret {
  key: string;
  title: string;
  set: boolean;
  lastModified: string | null;
}

export interface HealthResponse {
  ok: boolean;
  org_id: string;
  env: string;
  version: string;
  secrets: HealthSecret[];
}

export interface UniverseResponse {
  pool: unknown[];
  pool_source: string;
  watchlist: unknown;
  watchlist_source: string;
  min_avg_volume: unknown;
  positions: unknown[];
  positions_as_of: string | null;
  earnings: unknown[];
}

/** A row from the runs index (GET /runs → `runs[]`, GET /runs/{id} → `index`).
 *  The index table is schemaless beyond `run_id`; known fields are typed,
 *  the rest pass through. */
export interface RunIndexRow {
  run_id: string;
  org_id?: string;
  status?: string;
  artifact_uri?: string;
  completed_at?: string;
  [k: string]: unknown;
}

export interface RunsResponse {
  runs: RunIndexRow[];
}

export interface RunResponse {
  index: RunIndexRow;
  artifact: unknown;
}

/** A single measured routine pass from GET /usage (reads.ts getUsage → the
 *  usage store rows). `*_material_tokens` are null when a pass could not be
 *  measured (e.g. before the Anthropic key exists — the record path is
 *  fail-open, so a run still completes without a measurement). */
export interface UsagePass {
  run_kind: string;
  org_id: string;
  run_id: string;
  input_material_tokens: number | null;
  output_material_tokens: number | null;
}

/** A day-bucket from GET /usage: the summed material tokens across every pass
 *  that day, plus the per-pass breakdown. */
export interface UsageDay {
  date: string;
  total_input: number;
  total_output: number;
  passes: UsagePass[];
}

export interface UsageResponse {
  days: UsageDay[];
}

/** The verbatim plan trade embedded on a shadow-ledger position
 *  (reads.ts getShadow → item.trade). Known fields are typed; the rest pass
 *  through. `quantity`/`limit_price` arrive as either a number or a string
 *  depending on the source plan, so both are accepted. */
export interface ShadowTrade {
  trade_id?: string;
  action?: string;
  instrument?: string;
  underlying?: string;
  strategy?: string;
  quantity?: number | string;
  contract_symbol?: string;
  limit_price?: number | string;
  [k: string]: unknown;
}

/** A raw shadow-ledger item from GET /shadow (reads.ts getShadow). Numbers
 *  are stored in DynamoDB as STRINGS and surfaced verbatim, so the price/pnl
 *  fields are typed `string` (or `string | null`), NOT `number`. The item is
 *  schemaless beyond the known fields, like RunIndexRow. */
export interface ShadowPosition {
  data_key: string;
  status: 'open' | 'closed';
  opened_at: string;
  opened_run_id?: string;
  direction: string;
  entry: string;
  last_mark: string | null;
  last_marked_at: string | null;
  mark_stale: boolean;
  unrealized_pnl: string;
  trade: ShadowTrade;
  // closed items also carry:
  close_reason?: string;
  closed_at?: string;
  closed_run_id?: string;
  exit_price?: string;
  realized_pnl?: string;
  [k: string]: unknown;
}

export interface ShadowResponse {
  open: ShadowPosition[];
  closed: ShadowPosition[];
}

/** GET /strategy (reads.ts getStrategy) — the EXECUTED counterpart of the
 *  shadow ledger. Executed legs are tracked exactly like shadow legs, so the
 *  shape is structurally identical; the alias signals "same data, different
 *  ledger" and lets the UI reuse the shadow row rendering. Empty today (nothing
 *  has executed — shadow mode), populated once Phase 4 writes strategy rows. */
export type StrategyResponse = ShadowResponse;

// ---------------------------------------------------------------------------
// Team management shapes — mirror the console-api team.ts handlers (T2).
// ---------------------------------------------------------------------------

/** A member row from GET /members (team.ts getMembers): one identity with the
 *  set of member roles it holds in this org. */
export interface Member {
  identityId: string;
  walletAddress: string;
  roles: string[];
}

export interface MembersResponse {
  members: Member[];
}

/** An invite row from GET /invites (team.ts listInvites → Guardius listInvites),
 *  returned verbatim from Guardius — field names match the SDK `Invite` shape. */
export interface Invite {
  inviteId: string;
  roleIds: string[];
  maxUses: number;
  useCount: number;
  /** Epoch SECONDS (Guardius). */
  expiresAt: number;
  status?: 'active' | 'revoked';
  label?: string;
  [k: string]: unknown;
}

export interface InvitesResponse {
  invites: Invite[];
}

/** Body for POST /invites (team.ts mintInvite). */
export interface MintInviteInput {
  /** Member tier name (viewer/trader/owner); the console-API maps it to a Guardius role ID. */
  role: string;
  maxUses: number;
  expiresAt: string;
  label?: string;
}

/** Guardius 201 from POST /invites, returned verbatim. `code` is the one-time
 *  invite code shown to the owner ONCE (never persisted/re-fetchable). */
/** POST /invites response — the Guardius CreateInviteResponse: the invite fields
 *  flat, plus the raw one-time `code` (returned exactly once at mint). */
export interface MintInviteResult extends Invite {
  /** Raw invite code (gi_…), shown once. */
  code: string;
}

// ---------------------------------------------------------------------------
// Platform-admin org-creation invite shapes — mirror the console-api
// admin-invites.ts handlers (S3 a.2). These are NATIVE penseur-trading invites
// (a DynamoDB row keyed on the opaque `oci_…` code), NOT Guardius invites, so
// the fields are the snake_case DynamoDB row verbatim.
// ---------------------------------------------------------------------------

/** An org-creation invite row from GET /admin/org-invites (admin-invites.ts
 *  listOrgInvites → ddbScan). The opaque `invite_code` is BOTH the one-time code
 *  and the row's identity (used as the revoke path id). */
export interface OrgInvite {
  invite_code: string;
  status: 'active' | 'revoked';
  max_uses: number;
  use_count: number;
  /** Epoch SECONDS. */
  expires_at: number;
  created_by: string;
  created_at: string;
  label?: string;
  [k: string]: unknown;
}

export interface OrgInvitesResponse {
  invites: OrgInvite[];
}

/** Body for POST /admin/org-invites (admin-invites.ts mintOrgInvite). */
export interface MintOrgInviteInput {
  maxUses: number;
  ttlDays: number;
  label?: string;
}

/** POST /admin/org-invites 201 — the created DynamoDB invite row verbatim. The
 *  `invite_code` is the one-time code shown to the platform-admin at mint. */
export type MintOrgInviteResult = OrgInvite;

// ---------------------------------------------------------------------------
// Platform-admin org-request review shapes — mirror the console-api
// admin-requests.ts handlers (S3 c.1). These are the inbound org-creation
// requests staged by the onboarding wizard (b.1): the snake_case DynamoDB row
// keyed on `request_id`. The list/detail routes ALWAYS `redact()` the row, so
// `api_key`/`api_secret` are NEVER present here — secrets live only in SSM
// SecureString + the holdings CSV in S3, never on the row and never client-side.
// ---------------------------------------------------------------------------

/** An org-request row from GET /admin/org-requests (admin-requests.ts
 *  listOrgRequests) and GET /admin/org-requests/{id} (getOrgRequest), REDACTED.
 *  Known non-secret fields are typed; the rest pass through. NO secret field is
 *  ever part of this shape. */
export interface OrgRequest {
  request_id: string;
  org_id: string;
  status: string;
  owner_identity_id: string;
  owner_wallet?: string;
  org_name: string;
  env: string;
  /** The Alpaca BROKER account id — a non-secret reference, NOT a credential. */
  alpaca_account_id: string;
  invite_code?: string;
  tos_version?: string;
  tos_accepted_at?: string;
  csv_key?: string;
  created_at: string;
  /** Epoch SECONDS. */
  expires_at?: number;
  decided_by?: string;
  decided_at?: string;
  decision_note?: string;
  [k: string]: unknown;
}

export interface OrgRequestsResponse {
  requests: OrgRequest[];
}

/** Body for POST /admin/org-requests/{id}/approve|reject (admin-requests.ts) —
 *  the path `id` plus an optional decision note recorded on the row. */
export interface DecideOrgRequestInput {
  id: string;
  note?: string;
}

/** POST /admin/org-requests/{id}/approve 202 (admin-requests.ts approveOrgRequest)
 *  / .../reject 200 (rejectOrgRequest) — the decided request id + new status. */
export interface DecideOrgRequestResult {
  request_id: string;
  status: string;
}

// ---------------------------------------------------------------------------
// Onboarding intake shapes — mirror the console-api onboarding.ts handlers (S3
// b.1), the CUSTOMER-FACING surface of the org-creation pipeline. These ride the
// PUBLIC (validate-invite) + intake authorizer; the wizard SPA entry consumes
// them with an org-less token.
// ---------------------------------------------------------------------------

/** Body for POST /onboarding/validate-invite (PUBLIC). The `code` is the opaque
 *  `oci_…` org-creation invite code from the wizard's `?invite=` link. */
export interface ValidateInviteInput {
  code: string;
}

/** POST /onboarding/validate-invite 200 — oracle-poor: only `{ valid }`, never
 *  WHY (missing vs expired vs exhausted), so the namespace can't be probed. */
export interface ValidateInviteResult {
  valid: boolean;
}

/** Body for POST /onboarding/validate-alpaca. SECRET NOTE: api_key/api_secret
 *  travel ONLY in this mutation body (HTTPS, once) — never cached or key'd. */
export interface ValidateAlpacaInput {
  api_key: string;
  api_secret: string;
}

/** POST /onboarding/validate-alpaca 200 — the caller's OWN non-secret Alpaca
 *  account summary. `account_id` is DERIVED from the creds (never typed). */
export interface ValidateAlpacaResult {
  account_id: string;
  account_number: string;
  status: string;
  currency: string;
  buying_power: string;
}

/** Body for POST /onboarding/requests (onboarding.ts createOnboardingRequest).
 *  SECRET NOTE: `alpaca.api_key`/`api_secret` are POSTed once over HTTPS and
 *  staged server-side to SSM SecureString — NEVER logged, persisted to storage,
 *  or put in a query key client-side. `has_csv` toggles the presigned CSV PUT. */
export interface CreateOnboardingRequestInput {
  invite_code: string;
  org_name: string;
  env: string;
  tos_version: string;
  alpaca: { api_key: string; api_secret: string };
  has_csv: boolean;
}

/** POST /onboarding/requests 201 — the staged request id + new org id, plus an
 *  optional presigned `csvUploadUrl` (present only when `has_csv` was true). */
export interface CreateOnboardingRequestResult {
  request_id: string;
  org_id: string;
  csvUploadUrl?: string;
}

/** GET /onboarding/requests/mine 200 (onboarding.ts myOnboardingRequest) — the
 *  caller's LATEST request status. `owner_invite` (a ready-to-share owner
 *  sign-in URL) is present only once the org is provisioned (`status: 'active'`). */
export interface MyOnboardingRequest {
  status: string;
  org_id: string;
  request_id: string;
  owner_invite?: string;
}

// ---------------------------------------------------------------------------
// Api context — built from useAuth().
// ---------------------------------------------------------------------------

const ApiContext = createContext<Api | null>(null);

/** Provides the Api instance, wired to the auth layer's `ensureFreshToken`.
 *  The client awaits that getter before every request, so each request carries
 *  a token guaranteed valid for ≥60s — silently refreshing first if the current
 *  one is missing/expiring — or an empty header once the session is dead. */
export function ApiProvider({ children }: { children: ReactNode }) {
  const { config, ensureFreshToken } = useAuth();
  const api = useMemo(
    () => makeApi(config, ensureFreshToken),
    // `ensureFreshToken` is re-created every AuthProvider render, so this
    // rebuilds `makeApi` each render — cheap, and it guarantees we never
    // capture a stale closure. The getter itself reads live sessionStorage,
    // so a rebuilt instance is always correct.
    [config, ensureFreshToken],
  );
  return createElement(ApiContext.Provider, { value: api }, children);
}

export function useApi(): Api {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi must be used within an ApiProvider');
  return api;
}

// ---------------------------------------------------------------------------
// Query keys.
// ---------------------------------------------------------------------------

export const queryKeys = {
  settings: ['settings'] as const,
  health: ['health'] as const,
  universe: ['universe'] as const,
  shadow: ['shadow'] as const,
  strategy: ['strategy'] as const,
  runs: (limit: number) => ['runs', limit] as const,
  run: (id: string) => ['run', id] as const,
  usage: (from: string, to: string) => ['usage', from, to] as const,
  context: (artifact: string) => ['context', artifact] as const,
  members: ['members'] as const,
  invites: ['invites'] as const,
  orgInvites: ['org-invites'] as const,
  // Inbound org-creation requests (platform-admin review). Keyed by `status` so
  // the pending list and a (future) decided-status view don't collide. The
  // redacted rows carry no secret, so nothing sensitive is ever cached here.
  orgRequests: (status: string) => ['org-requests', status] as const,
  // The customer's own onboarding-request status (wizard pending screen). No
  // secret is ever keyed here — only the request status round-trip.
  myOnboardingRequest: ['onboarding', 'mine'] as const,
  // Watchtower telemetry connection state (GET /watchtower/status), read by the
  // Usage tab's WatchtowerCard. Carries no secret — only booleans plus the
  // public hosted-consent URL.
  watchtowerStatus: ['watchtower', 'status'] as const,
};

// ---------------------------------------------------------------------------
// Read hooks.
// ---------------------------------------------------------------------------

export function useSettings() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => api.get<SettingsResponse>('/settings'),
  });
}

export function useHealth() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => api.get<HealthResponse>('/health'),
  });
}

export function useUniverse() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.universe,
    queryFn: () => api.get<UniverseResponse>('/universe'),
  });
}

export function useRuns(limit = 20) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.runs(limit),
    queryFn: () => api.get<RunsResponse>(`/runs?limit=${limit}`),
  });
}

export function useRun(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.run(id),
    queryFn: () => api.get<RunResponse>(`/runs/${encodeURIComponent(id)}`),
    enabled: id.length > 0,
  });
}

/** GET /usage?from=&to= → per-day material-token usage over the window (reads.ts
 *  getUsage). Gated by `enabled` so nothing fires until both bounds are set. */
export function useUsage(from: string, to: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.usage(from, to),
    queryFn: () =>
      api.get<UsageResponse>(
        `/usage?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: Boolean(from && to),
  });
}

/** GET /shadow → open + recently-closed shadow ledger positions. Gated by
 *  `enabled` so the UI can fetch lazily, only once the user expands the
 *  positions panel (mirrors how useRun gates on `enabled`). */
export function useShadowPositions(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.shadow,
    queryFn: () => api.get<ShadowResponse>('/shadow'),
    enabled,
  });
}

/** GET /strategy → open + recently-closed STRATEGY ledger positions (the
 *  executed counterpart of /shadow). Gated by `enabled` so the UI fetches
 *  lazily, only once the user expands the Strategy panel — exactly like
 *  useShadowPositions. */
export function useStrategyPositions(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.strategy,
    queryFn: () => api.get<StrategyResponse>('/strategy'),
    enabled,
  });
}

/** GET /members → the org member roster (owner-gated route). Pass `enabled`
 *  so the Team tab only fetches for an owner (mirrors useShadowPositions). */
export function useMembers(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.members,
    queryFn: () => api.get<MembersResponse>('/members'),
    enabled,
  });
}

/** GET /invites → outstanding invites (owner-gated route). Gated by `enabled`
 *  like useMembers. */
export function useInvites(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.invites,
    queryFn: () => api.get<InvitesResponse>('/invites'),
    enabled,
  });
}

/** GET /admin/org-invites → outstanding org-creation invites (platform-admin
 *  gated route). Gated by `enabled` so the Admin tab only fetches for a platform
 *  admin (mirrors useInvites). */
export function useOrgInvites(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.orgInvites,
    queryFn: () => api.get<OrgInvitesResponse>('/admin/org-invites'),
    enabled,
  });
}

/** GET /admin/org-requests?status= → inbound org-creation requests (platform-
 *  admin gated route; default `pending`). Gated by `enabled` so the Admin tab
 *  only fetches for a platform admin (mirrors useOrgInvites). The list route
 *  reads `?status=` and returns redacted rows (never a secret). */
export function useOrgRequests(status = 'pending', enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.orgRequests(status),
    queryFn: () =>
      api.get<OrgRequestsResponse>(`/admin/org-requests?status=${encodeURIComponent(status)}`),
    enabled,
  });
}

/** Fetch several run artifacts in parallel (the Home feed's narrated beats).
 *  Shares queryKeys.run(id) with useRun, so a Home-prefetched artifact is a
 *  cache hit when the user clicks through to the Runs tab detail. */
export function useRunArtifacts(ids: string[]) {
  const api = useApi();
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: queryKeys.run(id),
      queryFn: () => api.get<RunResponse>(`/runs/${encodeURIComponent(id)}`),
    })),
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks. Each invalidates the queries it affects.
// ---------------------------------------------------------------------------

export interface PutSettingResult {
  key: string;
  value: unknown;
  previous: unknown;
}

/** PUT /settings/{key} body {"value": ...}. Invalidates settings (the row's
 *  value/source change) and health (secret presence dots derive from the same
 *  registry sweep). */
export function usePutSetting() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api.putJson<PutSettingResult>(`/settings/${encodeURIComponent(key)}`, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.settings });
      void qc.invalidateQueries({ queryKey: queryKeys.health });
    },
  });
}

export interface PutContextResult {
  artifact: string;
  status: string;
  bytes: number;
}

/** PUT /context/{artifact} raw text body, optional ?status=draft|approved.
 *  Invalidates that artifact's context query; for `watchlist` it also
 *  invalidates universe, since the Universe tab renders the watchlist. */
export function usePutContext() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ artifact, body, status }: {
      artifact: string;
      body: string;
      status?: 'draft' | 'approved';
    }) => {
      const path = status
        ? `/context/${encodeURIComponent(artifact)}?status=${status}`
        : `/context/${encodeURIComponent(artifact)}`;
      return api.putText<PutContextResult>(path, body);
    },
    onSuccess: (_data, { artifact }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.context(artifact) });
      if (artifact === 'watchlist') {
        void qc.invalidateQueries({ queryKey: queryKeys.universe });
      }
    },
  });
}

export interface PutPoolResult {
  count: number;
}

/** PUT /universe/pool body {"value": [...]}. Invalidates universe (pool is
 *  rendered there). */
export function usePutPool() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (value: unknown[]) => api.putJson<PutPoolResult>('/universe/pool', value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.universe });
    },
  });
}

/** POST /invites → mint a one-time invite (owner only). Invalidates the invites
 *  list on success so the new invite appears in the roster. The returned `code`
 *  is surfaced ONCE by the caller (never re-fetchable). */
export function useMintInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MintInviteInput) => api.post<MintInviteResult>('/invites', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.invites });
    },
  });
}

/** POST /invites/{id}/revoke → revoke an outstanding invite (owner only).
 *  Invalidates the invites list so the revoked invite drops out. */
export function useRevokeInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<unknown>(`/invites/${encodeURIComponent(id)}/revoke`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.invites });
    },
  });
}

/** POST /admin/org-invites → mint a one-time org-creation invite (platform-admin
 *  only). Invalidates the org-invites list on success so the new invite appears
 *  in the roster. The returned `invite_code` is surfaced ONCE by the caller. */
export function useMintOrgInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MintOrgInviteInput) =>
      api.post<MintOrgInviteResult>('/admin/org-invites', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.orgInvites });
    },
  });
}

/** POST /admin/org-invites/{id}/revoke → revoke an outstanding org-creation
 *  invite (platform-admin only). The `id` is the invite's opaque `invite_code`.
 *  Invalidates the org-invites list so the revoked invite drops out. */
export function useRevokeOrgInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ invite_code: string; status: string }>(
        `/admin/org-invites/${encodeURIComponent(id)}/revoke`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.orgInvites });
    },
  });
}

/** POST /admin/org-requests/{id}/approve → flip pending|failed → approved and
 *  fire async provisioning (platform-admin only). The optional `note` is recorded
 *  on the row as the decision note. Invalidates every org-requests status list so
 *  the request leaves `pending` once it transitions. */
export function useApproveOrgRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: DecideOrgRequestInput) =>
      api.post<DecideOrgRequestResult>(
        `/admin/org-requests/${encodeURIComponent(id)}/approve`,
        { note },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org-requests'] });
    },
  });
}

/** POST /admin/org-requests/{id}/reject → flip → rejected and tear down the
 *  staged secrets + CSV (platform-admin only). The optional `note` is recorded as
 *  the decision note. Invalidates every org-requests status list so the rejected
 *  request drops out of `pending`. */
export function useRejectOrgRequest() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: DecideOrgRequestInput) =>
      api.post<DecideOrgRequestResult>(
        `/admin/org-requests/${encodeURIComponent(id)}/reject`,
        { note },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org-requests'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Onboarding intake hooks (S3 b.2) — consumed by the onboarding wizard SPA
// entry. These ride an org-less token; they invalidate nothing in the main
// console (a customer mid-onboarding has no console org yet).
// ---------------------------------------------------------------------------

/** POST /onboarding/validate-invite (PUBLIC). A mutation (not a query) because
 *  the wizard fires it imperatively on mount with the URL's `?invite=` code and
 *  reacts to the `{ valid }` result. Oracle-poor by design — no retry storm:
 *  `retry: 0` so a transient failure surfaces as "invalid" rather than looping. */
export function useValidateInvite() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: ValidateInviteInput) =>
      api.post<ValidateInviteResult>('/onboarding/validate-invite', input),
    retry: 0,
  });
}

/** POST /onboarding/validate-alpaca (intake authorizer). Pre-submit check that
 *  returns the caller's non-secret Alpaca account summary so the wizard can
 *  confirm which account they connected. SECRET NOTE: it is a MUTATION (not a
 *  query) precisely so the key+secret are never cached or put in a query key. */
export function useValidateAlpaca() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: ValidateAlpacaInput) =>
      api.post<ValidateAlpacaResult>('/onboarding/validate-alpaca', input),
    retry: 0,
  });
}

/** POST /onboarding/requests (intake authorizer). SECRET NOTE: the Alpaca
 *  api_key/api_secret travel ONLY in this mutation's body (HTTPS, once) — never
 *  cached, never put in a query key, never persisted. The caller surfaces the
 *  returned `csvUploadUrl` to PUT the holdings CSV. */
export function useCreateOnboardingRequest() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: CreateOnboardingRequestInput) =>
      api.post<CreateOnboardingRequestResult>('/onboarding/requests', input),
  });
}

/** GET /onboarding/requests/mine (intake authorizer) → the caller's latest
 *  request status, polled on the wizard's pending screen. `refetchInterval`
 *  polls every 5s WHILE the request is still in flight (`pending`/`approved`);
 *  once it reaches a terminal state (`active`, or any non-in-flight status)
 *  polling stops. Gated by `enabled` so the wizard only polls after submit.
 *  A 404 (no request yet) is a normal pre-submit state, not an error to retry. */
export function useMyOnboardingRequest(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.myOnboardingRequest,
    queryFn: () => api.get<MyOnboardingRequest>('/onboarding/requests/mine'),
    enabled,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'approved' ? 5_000 : false;
    },
  });
}

/** Returns a function that seeds the `mine` status query with a just-created
 *  request, used by the wizard immediately after a successful submit.
 *
 *  Why this is needed: while a customer is signed in but has no request yet,
 *  {@link useMyOnboardingRequest} 404s and caches that error (`retry:false`, and
 *  its `refetchInterval` only polls a query whose `data.status` is
 *  `pending`/`approved` — an errored query has no data, so it NEVER polls). After
 *  a submit, nothing would refetch it, so the wizard's PendingScreen stuck on the
 *  "Submitting…" (404) fallback forever. Seeding the query with the created
 *  request (`status:'pending'`) clears the error, advances the screen, and starts
 *  the 5s poll that tracks the real status (pending → approved → active). */
export function useSeedMyOnboardingRequest(): (req: MyOnboardingRequest) => void {
  const qc = useQueryClient();
  return (req: MyOnboardingRequest) => {
    qc.setQueryData(queryKeys.myOnboardingRequest, req);
  };
}

// ---------------------------------------------------------------------------
// Convenience root provider: QueryClientProvider + ApiProvider.
// ---------------------------------------------------------------------------

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
    },
  });
}

export function QueryProvider({
  client,
  children,
}: {
  client: QueryClient;
  children: ReactNode;
}) {
  return createElement(
    QueryClientProvider,
    { client },
    createElement(ApiProvider, null, children),
  );
}
